import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calculator } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";
import { useLanguageAwareSEO } from "@/hooks/useLanguageAwareSEO";
import { breadcrumbSchema, calculatorToolSchema } from "@/utils/structuredData";

export default function FrequencyWavelengthCalculator() {
  useLanguageAwareSEO([
    (lang) => breadcrumbSchema([
      { name: "Home", url: "https://calclab.app" },
      { name: "Physics", url: "https://calclab.app/physics" },
      { name: "Frequency & Wavelength Calculator", url: "https://calclab.app/calculator/frequency-wavelength" }
    ], lang),
    (lang) => calculatorToolSchema(
      "Frequency & Wavelength Calculator",
      "Convert between frequency and wavelength for waves. Free online physics calculator.",
      "https://calclab.app/calculator/frequency-wavelength",
      lang
    )
  ]);
  const [frequency, setFrequency] = useState("");
  const [wavelength, setWavelength] = useState("");
  const [waveSpeed, setWaveSpeed] = useState("343");
  const [resultFreq, setResultFreq] = useState<number | null>(null);
  const [resultWave, setResultWave] = useState<number | null>(null);

  const calculateFromFrequency = () => {
    const f = parseFloat(frequency);
    const v = parseFloat(waveSpeed);

    if (isNaN(f) || isNaN(v) || f === 0) {
      toast.error("Please enter valid values");
      return;
    }

    const lambda = v / f;
    setResultWave(lambda);
    toast.success("Calculation complete!");
  };

  const calculateFromWavelength = () => {
    const lambda = parseFloat(wavelength);
    const v = parseFloat(waveSpeed);

    if (isNaN(lambda) || isNaN(v) || lambda === 0) {
      toast.error("Please enter valid values");
      return;
    }

    const f = v / lambda;
    setResultFreq(f);
    toast.success("Calculation complete!");
  };

  const reset = () => {
    setFrequency("");
    setWavelength("");
    setWaveSpeed("343");
    setResultFreq(null);
    setResultWave(null);
  };

  return (
    <PageLayout calculatorName="Frequency & Wavelength Calculator" calculatorPath="/calculator/frequency-wavelength">
      <div className="max-w-2xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">Frequency & Wavelength Calculator</h1>
          <p className="text-muted-foreground">
            Convert between frequency and wavelength
          </p>
        </div>

        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>Formula: f = v/λ or λ = v/f</CardTitle>
            <CardDescription>
              Where f is frequency (Hz), λ is wavelength (m), v is wave speed (m/s)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="waveSpeed">Wave Speed (m/s)</Label>
              <Input
                id="waveSpeed"
                type="number"
                step="0.01"
                placeholder="Speed of sound: 343 m/s"
                value={waveSpeed}
                onChange={(e) => setWaveSpeed(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">Default: 343 m/s (sound in air)</p>
            </div>

            <Tabs defaultValue="frequency" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="frequency">Find Wavelength</TabsTrigger>
                <TabsTrigger value="wavelength">Find Frequency</TabsTrigger>
              </TabsList>

              <TabsContent value="frequency" className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="frequency">Frequency (Hz)</Label>
                  <Input
                    id="frequency"
                    type="number"
                    step="0.01"
                    placeholder="Enter frequency"
                    value={frequency}
                    onChange={(e) => setFrequency(e.target.value)}
                  />
                </div>

                <div className="flex gap-3">
                  <Button onClick={calculateFromFrequency} className="flex-1">
                    <Calculator className="mr-2 h-4 w-4" />
                    Calculate
                  </Button>
                  <Button onClick={reset} variant="outline">
                    Reset
                  </Button>
                </div>

                {resultWave !== null && (
                  <>
                    <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                      <CardHeader>
                        <CardTitle className="text-xl">Result</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-3xl font-bold text-primary">
                          {resultWave.toFixed(4)} m
                        </p>
                        <p className="text-sm text-muted-foreground mt-2">
                          Wavelength
                        </p>
                      </CardContent>
                    </Card>
                    
                    <CalculatorSuperCalcCTA config={calculatorCTAs["frequency-wavelength"]} trigger={resultWave} />
                  </>
                )}
              </TabsContent>

              <TabsContent value="wavelength" className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="wavelength">Wavelength (m)</Label>
                  <Input
                    id="wavelength"
                    type="number"
                    step="0.01"
                    placeholder="Enter wavelength"
                    value={wavelength}
                    onChange={(e) => setWavelength(e.target.value)}
                  />
                </div>

                <div className="flex gap-3">
                  <Button onClick={calculateFromWavelength} className="flex-1">
                    <Calculator className="mr-2 h-4 w-4" />
                    Calculate
                  </Button>
                  <Button onClick={reset} variant="outline">
                    Reset
                  </Button>
                </div>

                {resultFreq !== null && (
                  <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                    <CardHeader>
                      <CardTitle className="text-xl">Result</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold text-primary">
                        {resultFreq.toFixed(2)} Hz
                      </p>
                      <p className="text-sm text-muted-foreground mt-2">
                        Frequency
                      </p>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>
            </Tabs>

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-lg">How to use</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm text-muted-foreground">
                <p>1. Enter wave speed (default is sound in air at 343 m/s)</p>
                <p>2. Choose which value you want to calculate</p>
                <p>3. Enter the known value and click Calculate</p>
                <p className="pt-2 font-medium text-foreground">
                  Example: 440 Hz at 343 m/s = 0.78 m wavelength (A4 note)
                </p>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
